// client/src/lib/money.js
// Centralised money formatting (group-aware)
// Rule: currency must never be hardcoded in UI. Always pass group.currency_code (or API currency_code).

const LOCALE_BY_COUNTRY = {
  AU: 'en-AU',
  NZ: 'en-NZ',
  US: 'en-US',
  CA: 'en-CA',
  GB: 'en-GB',
  IE: 'en-IE',
  IN: 'en-IN',

  // Middle East (English UI)
  AE: 'en-AE',
  SA: 'en-SA',
  QA: 'en-QA',
  KW: 'en-KW',
  BH: 'en-BH',
  OM: 'en-OM',
};

export function localeForCountry(countryCode) {
  const cc = String(countryCode || '').toUpperCase();
  return LOCALE_BY_COUNTRY[cc] || (typeof navigator !== 'undefined' ? navigator.language : 'en');
}

export function formatMoney(
  amount,
  {
    currencyCode,
    countryCode,
    locale,
    // Prefer currency symbols where available (e.g., INR -> ₹) even if the viewer's locale
    // would otherwise display a currency code (e.g., "INR 10,000.00" in en-AU).
    // Callers can override this by passing currencyDisplay: 'code' when needed.
    currencyDisplay = 'narrowSymbol',
    minimumFractionDigits,
    maximumFractionDigits = 2,
    fallback = '—',
  } = {}
) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return fallback;

  const ccy = String(currencyCode || '').toUpperCase();
  const loc = locale || localeForCountry(countryCode);

  // If currency missing, DO NOT guess. Return plain number.
  if (!ccy) {
    try {
      return new Intl.NumberFormat(loc, {
        maximumFractionDigits,
        ...(typeof minimumFractionDigits === 'number' ? { minimumFractionDigits } : {}),
      }).format(n);
    } catch {
      return String(n);
    }
  }

  try {
    return new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: ccy,
      currencyDisplay,
      maximumFractionDigits,
      ...(typeof minimumFractionDigits === 'number' ? { minimumFractionDigits } : {}),
    }).format(n);
  } catch {
    // fallback: amount + code (still no guessing)
    return `${n} ${ccy}`;
  }
}

export function formatMoneyForGroup(amount, group, opts = {}) {
  const g = group || {};
  return formatMoney(amount, {
    currencyCode: g.currency_code || g.currencyCode,
    countryCode: g.country_code || g.countryCode,
    ...opts,
  });
}
